import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Input, Popover, Select, Typography, App } from 'antd';
import type { MenuProps } from 'antd';
import { ExportOutlined, ImportOutlined, InfoCircleOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { CustomerRule, MethodName, RuleFor } from '../../../../dto/admin-rule-models';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../../../authConfig';
import { CustomerRuleCard } from './customer-rule-card';
import { CustomerRuleForm } from './customer-rule-form';
import { getCustomerRules, postCustomerRules } from '../../../../services/admin-page-service';
import { SalesChannel } from '../../../../dto/model';
import { salesChannelLabel } from '../../../../dto/qualifying-flow-models';
import { handleDownloadAllJson, handleJsonImport } from '../../../../utils/file-utils';
import { strings } from '../../../../lang';
import { useHeaderState } from '../../../../store/header-state';
import { useShallow } from 'zustand/shallow';
import './customer-rule.css';

const { Title, Text } = Typography;

const visualizeRuleForFilter = (ruleFor: RuleFor): string => {
  const cjp = strings.customerJourneyPlanner;
  switch (ruleFor) {
    case RuleFor.OpportunitySync: return cjp.outcomeOpportunitySync;
    case RuleFor.CalendarDisplay: return cjp.outcomeCalendarDisplay;
    case RuleFor.SlotBooking: return cjp.outcomeSlotBooking;
    case RuleFor.AgentsView: return cjp.outcomeAgentsView;
    default: return ruleFor;
  }
};

const ruleMatchesSearch = (rule: CustomerRule, text: string): boolean => {
  const query = text.trim().toLocaleLowerCase();
  if (!query) return true;

  return [
    rule.title,
    rule.description,
    ...rule.conditions.flatMap((condition) => [condition.propertyName, condition.value]),
    ...rule.outcomes.flatMap((outcome) => [outcome.action, outcome.actionValue, visualizeRuleForFilter(outcome.ruleFor)]),
  ].some((value) => value?.toLocaleLowerCase().includes(query));
};

const getSuggestedRulePrefix = (rules: CustomerRule[]): string => {
  const usedPrefixes = new Set(
    rules
      .filter((rule) => !rule.isDeleted)
      .map((rule) => rule.title.match(/^(\d+)[a-zA-Z]?\s+/)?.[1])
      .filter((prefix): prefix is string => !!prefix)
      .map(Number),
  );

  let nextPrefix = 1;
  while (usedPrefixes.has(nextPrefix)) nextPrefix++;

  return String(nextPrefix).padStart(2, '0');
};

export const AdminCustomerRule = () => {
  useHeaderState(useShallow((state) => state.language));
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [customerRules, setCustomerRules] = useState<CustomerRule[]>([]);
  const [displayRuleForm, shouldDisplayRuleForm] = useState<boolean>(false);
  const [displayAddButton, shouldDisplayAddButton] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<RuleFor[]>([]);
  const [salesChannelFilter, setSalesChannelFilter] = useState<SalesChannel[]>([]);

  const anyFilterActive = !!searchText || outcomeFilter.length > 0 || salesChannelFilter.length > 0;

  useEffect(() => {
    fetchCustomerRules();
  }, []);

  const fetchCustomerRules = async () => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const customerRules = await getCustomerRules(token.accessToken);
      if (customerRules.isSuccess) {
        setCustomerRules(customerRules.data!);
        return;
      }

      notification.error({
        message: strings.customerJourneyPlanner.loadFailed,
        description: customerRules.errors?.join(', ') || strings.customerJourneyPlanner.loadFailedDescription,
      });
    } catch (e: any) {
      notification.error({
        message: strings.customerJourneyPlanner.loadFailed,
        description: e.message || String(e),
      });
    }
  };

  const onSubmit = async (rule: CustomerRule) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await postCustomerRules(token.accessToken, [rule]);

      if (result.isSuccess) {
        notification.success({ message: strings.customerJourneyPlanner.savedSuccessfully });
        await fetchCustomerRules();
        shouldDisplayRuleForm(false);
        shouldDisplayAddButton(true);
      } else {
        notification.error({
          message: strings.customerJourneyPlanner.saveFailed,
          description: result.errors?.join(', ') || strings.customerJourneyPlanner.unknownError,
        });
      }
    } catch (e: any) {
      notification.error({
        message: strings.customerJourneyPlanner.saveFailed,
        description: e.message || String(e),
      });
    }
  };

  const onCancel = () => {
    const updatedRules = customerRules.filter((r) => r.id !== 0);
    shouldDisplayRuleForm(false);
    shouldDisplayAddButton(true);
    setCustomerRules(updatedRules);
  };

  const addCustomerRule = () => {
    const newRule = {
      id: 0,
      feId: crypto.randomUUID(),
      title: `${getSuggestedRulePrefix(customerRules)} `,
      description: '',
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdBy: '',
      isDeleted: false,
      methodNames: [MethodName.NONE],
      salesChannels: [SalesChannel.DigitalSales],
      conditions: [],
      outcomes: [],
    };
    setCustomerRules([...customerRules, newRule]);
    shouldDisplayRuleForm(true);
    shouldDisplayAddButton(false);
  };

  const exportRules = async () => {
    await handleDownloadAllJson(customerRules, 'customer_rules_export');
  };

  const handleImport = () => {
    handleJsonImport<CustomerRule[]>(async (data) => {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      await postCustomerRules(token.accessToken, data);
      const customerRules = await getCustomerRules(token.accessToken);
      if (customerRules.isSuccess) {
        setCustomerRules(customerRules.data!);
      }
    });
  };

  const onDuplicate = (duplicatedRule: CustomerRule) => {
    setCustomerRules([...customerRules, duplicatedRule]);
    shouldDisplayRuleForm(true);
    shouldDisplayAddButton(false);
  };

  const importExportItems: MenuProps['items'] = [
    {
      key: 'export',
      label: strings.customerJourneyPlanner.exportRules,
      icon: <ExportOutlined />,
      onClick: exportRules,
    },
    ...(process.env.REACT_APP_ENV !== 'production'
      ? [{ key: 'import', label: strings.customerJourneyPlanner.importRules, icon: <ImportOutlined />, onClick: handleImport }]
      : []),
  ];

  const filteredAndSortedRules = useMemo(() => {
    return customerRules
      .filter((rule) => !rule.isDeleted)
      .filter((rule) => salesChannelFilter.length === 0 || rule.salesChannels?.some((channel) => salesChannelFilter.includes(channel)))
      .filter((rule) => outcomeFilter.length === 0 || rule.outcomes.some((outcome) => outcomeFilter.includes(outcome.ruleFor)))
      .filter((rule) => ruleMatchesSearch(rule, searchText))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [customerRules, outcomeFilter, salesChannelFilter, searchText]);

  const visibleRulesCount = customerRules.filter((rule) => !rule.isDeleted).length;

  const cjp = strings.customerJourneyPlanner;

  return (
    <div className="customer-journey-planner container flex-admin-vertical-container" style={{ width: 900, justifyContent: 'flex-start' }}>
      <div className="customer-rule__page-header">
        <div>
          <Title className="customer-rule__page-title" level={4}>
            Customer Journey Planner
          </Title>
          <Text className="customer-rule__page-description" type="secondary">
            {cjp.summary}
            <Popover content={cjp.ruleOrderHelpContent} trigger={['click']}>
              <Button type="link" className="customer-rule__order-help" icon={<InfoCircleOutlined />}>
                {cjp.ruleOrderHelp}
              </Button>
            </Popover>
          </Text>
        </div>
        <Dropdown menu={{ items: importExportItems }} trigger={['click']}>
          <Button aria-label={cjp.moreActions} className="customer-rule__page-actions" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </div>

      {displayAddButton && (
        <div className="customer-rule__list-controls">
          <div className="customer-rule__filters">
            <div className="customer-rule__search-summary">
              <Input
                aria-label={cjp.searchPlaceholder}
                type="search"
                prefix={<SearchOutlined />}
                placeholder={cjp.searchPlaceholder}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                allowClear
                className="customer-rule__search"
              />
              <Text className="customer-rule__result-count" type="secondary">
                {cjp.resultsCount.replace('{count}', String(filteredAndSortedRules.length)).replace('{total}', String(visibleRulesCount))}
              </Text>
            </div>
            <Select
              aria-label={cjp.filterBySalesChannel}
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
              options={Object.values(SalesChannel)
                .filter((channel) => channel !== SalesChannel.MissingQueue)
                .map((value) => ({ label: salesChannelLabel[value], value }))}
              value={salesChannelFilter}
              onChange={setSalesChannelFilter}
              placeholder={cjp.filterSalesChannel}
              allowClear
              className="customer-rule__sales-channel-filter"
            />
            <Select
              aria-label={cjp.filterByOutcome}
              mode="multiple"
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
              options={Object.values(RuleFor).map((value) => ({ label: visualizeRuleForFilter(value), value }))}
              value={outcomeFilter}
              onChange={setOutcomeFilter}
              placeholder={cjp.filterPlaceholder}
              allowClear
              className="customer-rule__outcome-filter"
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={addCustomerRule}>
              {cjp.addRuleLabel}
            </Button>
          </div>
        </div>
      )}

      {displayRuleForm && <CustomerRuleForm rule={customerRules.find((x) => +x.id === 0)!} onSubmit={onSubmit} onCancel={onCancel} />}
      {!displayRuleForm &&
        filteredAndSortedRules.map((rule) => (
          <CustomerRuleCard key={rule.id} rule={rule} onSubmit={onSubmit} onDuplicate={onDuplicate} shouldDisplayAddButton={shouldDisplayAddButton} />
        ))}
      {!displayRuleForm && anyFilterActive && filteredAndSortedRules.length === 0 && (
        <div className="customer-rule__empty-state">
          <Text type="secondary">{cjp.noRulesMatched}</Text>
        </div>
      )}
    </div>
  );
};
